import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

export type ThemeVars = Record<string, string>;
export type ThemeName = "Dark" | "Light" | "Ocean" | "KidFun" | "Theme1" | "Midnight" | "Candy" | "Solar";

interface ThemeContextValue {
  themeName: string;
  vars: ThemeVars;
  applyTheme: (vars: ThemeVars, name?: string) => void;
  resetToPreset: (name: string) => void;

  exportJSON: () => string;
  importJSONText: (json: string) => void;

  savedNames: string[];
  saveAs: (name: string) => void;
  loadSaved: (name: string) => void;
  deleteSaved: (name: string) => void;

  setFont: (family: string) => void;
  currentFont: string;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);
export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
};

const STORAGE_ACTIVE = "admin-theme-active";
const STORAGE_SAVED = "admin-theme-saved";
const STORAGE_FONT  = "admin-theme-font";

/* ensure new keys exist; all colors are FULL `hsl(...)` strings */
const normalize = (v: ThemeVars): ThemeVars => ({
  "--dashboard-primary-fg": v["--dashboard-primary-fg"] ?? "hsl(0 0% 100%)",
  "--dashboard-accent-fg": v["--dashboard-accent-fg"] ?? (v["--dashboard-text"] ?? "hsl(210 40% 98%)"),

  "--dashboard-chip-bg": v["--dashboard-chip-bg"] ?? (v["--dashboard-surface-3"] ?? "hsl(270 70% 55%)"),
  "--dashboard-chip-fg": v["--dashboard-chip-fg"] ?? (v["--dashboard-text"] ?? "hsla(298, 66%, 69%, 1.00)"),
  "--dashboard-chip-border": v["--dashboard-chip-border"] ?? (v["--dashboard-border"] ?? "hsl(217 32% 23%)"),

  "--dashboard-shadow": v["--dashboard-shadow"] ?? "hsl(220 40% 2%)",
  "--dashboard-shadow-1a": v["--dashboard-shadow-1a"] ?? "0.25",
  "--dashboard-shadow-2a": v["--dashboard-shadow-2a"] ?? "0.30",
  "--dashboard-shadow-3a": v["--dashboard-shadow-3a"] ?? "0.35",

  "--dashboard-radius": v["--dashboard-radius"] ?? "12px",
  "--dashboard-radius-sm": v["--dashboard-radius-sm"] ?? "8px",
  "--dashboard-radius-lg": v["--dashboard-radius-lg"] ?? "16px",

  ...v,
});

/* ===== Presets (now written as `hsl(...)` strings) ===== */
export const THEME_PRESETS: Record<ThemeName, ThemeVars> = {
  Dark: normalize({
    "--dashboard-primary": "hsl(240 100% 65%)",
    "--dashboard-secondary": "hsl(250 85% 70%)",
    "--dashboard-accent": "hsl(160 84% 39%)",
    "--dashboard-danger": "hsl(0 72% 51%)",
    "--dashboard-warning": "hsl(38 92% 50%)",
    "--dashboard-bg": "hsl(222 47% 7%)",
    "--dashboard-surface": "hsl(222 47% 11%)",
    "--dashboard-surface-2": "hsl(220 39% 15%)",
    "--dashboard-surface-3": "hsl(220 34% 18%)",
    "--dashboard-text": "hsl(210 40% 98%)",
    "--dashboard-text-muted": "hsl(215 25% 65%)",
    "--dashboard-text-subtle": "hsl(215 15% 50%)",
    "--dashboard-border": "hsl(217 32% 23%)",
    "--dashboard-input-bg": "hsl(220 39% 13%)",
    "--dashboard-chip-fg": "hsla(0, 0%, 100%, 1.00)",
    "--dashboard-chip-bg": "hsla(270, 52%, 72%, 1.00)",
    "--dashboard-chip-border": "hsl(0, 0%, 0%)",    
  }),
  Light: normalize({
    "--dashboard-primary": "hsl(240 90% 56%)",
    "--dashboard-secondary": "hsl(260 83% 66%)",
    "--dashboard-accent": "hsl(160 63% 41%)",
    "--dashboard-danger": "hsl(0 84% 60%)",
    "--dashboard-warning": "hsl(38 92% 50%)",
    "--dashboard-bg": "hsl(0 0% 100%)",
    "--dashboard-surface": "hsl(0 0% 100%)",
    "--dashboard-surface-2": "hsl(210 40% 98%)",
    "--dashboard-surface-3": "hsl(220 14% 96%)",
    "--dashboard-text": "hsl(222 47% 11%)",
    "--dashboard-text-muted": "hsl(215 16% 46%)",
    "--dashboard-text-subtle": "hsl(215 10% 36%)",
    "--dashboard-border": "hsl(214 32% 91%)",
    "--dashboard-input-bg": "hsl(0 0% 100%)",
    "--dashboard-chip-fg": "hsla(0, 0%, 100%, 1.00)",
    "--dashboard-chip-bg": "hsla(270, 52%, 72%, 1.00)",
    "--dashboard-chip-border": "hsl(0, 0%, 0%)",  
  }),
  Ocean: normalize({
    "--dashboard-primary": "hsl(201 96% 32%)",
    "--dashboard-secondary": "hsl(189 94% 43%)",
    "--dashboard-accent": "hsl(171 72% 41%)",
    "--dashboard-danger": "hsl(2 86% 58%)",
    "--dashboard-warning": "hsl(45 93% 47%)",
    "--dashboard-bg": "hsl(215 45% 8%)",
    "--dashboard-surface": "hsl(216 50% 10%)",
    "--dashboard-surface-2": "hsl(215 44% 13%)",
    "--dashboard-surface-3": "hsl(215 40% 17%)",
    "--dashboard-text": "hsl(210 40% 98%)",
    "--dashboard-text-muted": "hsl(215 25% 70%)",
    "--dashboard-text-subtle": "hsl(215 20% 60%)",
    "--dashboard-border": "hsl(217 32% 23%)",
    "--dashboard-input-bg": "hsl(215 44% 12%)",
    "--dashboard-chip-fg": "hsla(0, 0%, 100%, 1.00)",
    "--dashboard-chip-bg": "hsla(270, 52%, 72%, 1.00)",
    "--dashboard-chip-border": "hsl(0, 0%, 0%)",  
  }),
  KidFun: normalize({
    "--dashboard-primary": "hsl(340 82% 52%)",
    "--dashboard-secondary": "hsl(45 100% 50%)",
    "--dashboard-accent": "hsl(160 84% 39%)",
    "--dashboard-danger": "hsl(0 84% 60%)",
    "--dashboard-warning": "hsl(38 92% 50%)",
    "--dashboard-bg": "hsl(215 45% 8%)",
    "--dashboard-surface": "hsl(218 47% 12%)",
    "--dashboard-surface-2": "hsl(218 44% 16%)",
    "--dashboard-surface-3": "hsl(218 40% 19%)",
    "--dashboard-text": "hsl(210 40% 98%)",
    "--dashboard-text-muted": "hsl(215 25% 70%)",
    "--dashboard-text-subtle": "hsl(215 20% 60%)",
    "--dashboard-border": "hsl(217 32% 23%)",
    "--dashboard-input-bg": "hsl(218 44% 14%)",
    "--dashboard-chip-fg": "hsla(0, 0%, 100%, 1.00)",
    "--dashboard-chip-bg": "hsla(270, 52%, 72%, 1.00)",
    "--dashboard-chip-border": "hsl(0, 0%, 0%)",  
  }),
  Theme1: normalize({
    "--dashboard-primary": "hsl(247 32% 51%)",
    "--dashboard-secondary": "hsl(326 35% 60%)",
    "--dashboard-accent": "hsl(227 69% 34%)",
    "--dashboard-danger": "hsl(0 72% 51%)",
    "--dashboard-warning": "hsl(38 92% 50%)",
    "--dashboard-bg": "hsl(221 25% 24%)",
    "--dashboard-surface": "hsl(195 48% 2%)",
    "--dashboard-surface-2": "hsl(233 35% 16%)",
    "--dashboard-surface-3": "hsl(233 31% 31%)",
    "--dashboard-text": "hsl(273 29% 61%)",
    "--dashboard-text-muted": "hsl(195 72% 69%)",
    "--dashboard-text-subtle": "hsl(215 35% 29%)",
    "--dashboard-border": "hsl(279 32% 23%)",
    "--dashboard-input-bg": "hsl(180 28% 21%)",
    "--dashboard-chip-fg": "hsla(0, 0%, 100%, 1.00)",
    "--dashboard-chip-bg": "hsla(270, 52%, 72%, 1.00)",
    "--dashboard-chip-border": "hsl(0, 0%, 0%)",   
  }),
  Midnight: normalize({
    "--dashboard-primary": "hsl(265 89% 66%)",
    "--dashboard-accent": "hsl(190 90% 45%)",
    "--dashboard-danger": "hsl(0 75% 62%)",
    "--dashboard-warning": "hsl(42 92% 56%)",
    "--dashboard-bg": "hsl(223 40% 6%)",
    "--dashboard-surface": "hsl(223 42% 9%)",
    "--dashboard-surface-2": "hsl(223 38% 13%)",
    "--dashboard-surface-3": "hsl(223 34% 17%)",
    "--dashboard-text": "hsl(210 40% 98%)",
    "--dashboard-text-muted": "hsl(220 14% 75%)",
    "--dashboard-text-subtle": "hsl(220 12% 62%)",
    "--dashboard-border": "hsl(223 24% 24%)",
    "--dashboard-input-bg": "hsl(223 36% 11%)",
    "--dashboard-chip-fg": "hsla(0, 0%, 100%, 1.00)",
    "--dashboard-chip-bg": "hsla(270, 52%, 72%, 1.00)",
    "--dashboard-chip-border": "hsl(0, 0%, 0%)",  
  }),
  Candy: normalize({
    "--dashboard-primary": "hsl(318 90% 64%)",
    "--dashboard-accent": "hsl(192 92% 48%)",
    "--dashboard-danger": "hsl(0 84% 60%)",
    "--dashboard-warning": "hsl(43 96% 56%)",
    "--dashboard-bg": "hsl(260 25% 14%)",
    "--dashboard-surface": "hsl(260 22% 18%)",
    "--dashboard-surface-2": "hsl(258 20% 22%)",
    "--dashboard-surface-3": "hsl(258 18% 26%)",
    "--dashboard-text": "hsl(0 0% 98%)",
    "--dashboard-text-muted": "hsl(260 15% 75%)",
    "--dashboard-text-subtle": "hsl(260 12% 62%)",
    "--dashboard-border": "hsl(258 12% 32%)",
    "--dashboard-input-bg": "hsl(258 18% 20%)",
    "--dashboard-chip-fg": "hsla(0, 0%, 100%, 1.00)",
    "--dashboard-chip-bg": "hsla(270, 52%, 72%, 1.00)",
    "--dashboard-chip-border": "hsl(0, 0%, 0%)",  
  }),
  Solar: normalize({
    "--dashboard-primary": "hsl(25 95% 55%)",
    "--dashboard-accent": "hsl(160 63% 41%)",
    "--dashboard-danger": "hsl(347 88% 46%)",
    "--dashboard-warning": "hsl(50 98% 52%)",
    "--dashboard-bg": "hsl(45 25% 96%)",
    "--dashboard-surface": "hsl(0 0% 100%)",
    "--dashboard-surface-2": "hsl(45 28% 94%)",
    "--dashboard-surface-3": "hsl(45 22% 90%)",
    "--dashboard-text": "hsl(222 47% 11%)",
    "--dashboard-text-muted": "hsl(215 16% 46%)",
    "--dashboard-text-subtle": "hsl(215 10% 36%)",
    "--dashboard-border": "hsl(45 12% 82%)",
    "--dashboard-input-bg": "hsl(0 0% 100%)",
    "--dashboard-chip-fg": "hsla(0, 0%, 100%, 1.00)",
    "--dashboard-chip-bg": "hsla(270, 52%, 72%, 1.00)",
    "--dashboard-chip-border": "hsl(0, 0%, 0%)",  
  }),
};

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [themeName, setThemeName] = useState<string>("Dark");
  const [vars, setVars] = useState<ThemeVars>(THEME_PRESETS.Dark);
  const [saved, setSaved] = useState<Record<string, ThemeVars>>({});
  const [currentFont, setCurrentFont] = useState<string>("Inter:300..700");

  useEffect(() => {
    try {
      const active = localStorage.getItem(STORAGE_ACTIVE);
      const savedAll = localStorage.getItem(STORAGE_SAVED);
      const font = localStorage.getItem(STORAGE_FONT);
      if (savedAll) setSaved(JSON.parse(savedAll));
      if (font) loadGoogleFont(font);
      if (active) {
        const { name, vars } = JSON.parse(active) as { name: string; vars: ThemeVars };
        setThemeName(name); setVars(normalize(vars)); applyVars(normalize(vars));
        return;
      }
    } catch {}
    applyVars(vars);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyVars = (map: ThemeVars) => {
    const root = document.querySelector(".admin-dashboard") as HTMLElement | null;
    if (!root) return;
    const norm = normalize(map);
    Object.entries(norm).forEach(([k, v]) => root.style.setProperty(k, v));
  };

  const applyTheme = (map: ThemeVars, name: string = themeName) => {
    const n = normalize(map);
    setVars(n); setThemeName(name); applyVars(n);
    localStorage.setItem(STORAGE_ACTIVE, JSON.stringify({ name, vars: n }));
  };

  const resetToPreset = (name: string) => applyTheme(THEME_PRESETS[name as ThemeName], name);

  const exportJSON = () => JSON.stringify({ name: themeName, vars }, null, 2);
  const importJSONText = (json: string) => {
    const parsed = JSON.parse(json) as { name?: string; vars: ThemeVars };
    applyTheme(parsed.vars, parsed.name ?? themeName);
  };

  const saveAs = (name: string) => {
    const next = { ...saved, [name]: vars };
    setSaved(next);
    localStorage.setItem(STORAGE_SAVED, JSON.stringify(next));
  };
  const loadSaved = (name: string) => { const v = saved[name]; if (v) applyTheme(v, name); };
  const deleteSaved = (name: string) => {
    const next = { ...saved }; delete next[name];
    setSaved(next); localStorage.setItem(STORAGE_SAVED, JSON.stringify(next));
  };
  const savedNames = useMemo(() => Object.keys(saved).sort(), [saved]);

  /* Google Fonts loader */
  function loadGoogleFont(familyAndAxis: string) {
    // example: "Inter:300..700" or "Rubik:300..900"
    const [fam, axis] = familyAndAxis.split(":");
    const url = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fam)}:${axis?`wght@${axis}`:"wght@400;600;700"}&display=swap`;
    let link = document.getElementById("theme-font") as HTMLLinkElement | null;
    if (!link) { link = document.createElement("link"); link.id="theme-font"; link.rel="stylesheet"; document.head.appendChild(link); }
    link.href = url;
    const root = document.querySelector(".admin-dashboard") as HTMLElement | null;
    if (root) root.style.setProperty("--font-sans", `"${fam}", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial`);
    setCurrentFont(familyAndAxis);
    localStorage.setItem(STORAGE_FONT, familyAndAxis);
  }

  const value = useMemo(() => ({
    themeName, vars, applyTheme, resetToPreset,
    exportJSON, importJSONText, savedNames, saveAs, loadSaved, deleteSaved,
    setFont: loadGoogleFont, currentFont,
  }), [themeName, vars, savedNames, currentFont]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
